import { z } from 'zod';
import { Id } from '../primitives.js';
import { Fdi } from './dental-chart.js';

/**
 * Контракты внутреннего чата — обсуждение внутри карточки пациента или
 * наряда лаборатории, не отдельный мессенджер.
 *
 * Канал не создаётся отдельной операцией: клиент отправляет первое сообщение
 * по `(subjectType, subjectId)`, и сервер заводит канал сам, если его ещё нет
 * (`SendChatMessageRequest` не несёт `channelId` вовсе). Так снят единственный
 * риск гонки лениво создаваемой сущности — два устройства без сети не
 * разойдутся, угадывая разные идентификаторы одного и того же канала: угадывать
 * нечего, потому что канал не идентификатор запроса, а его следствие.
 *
 * `ChatMessage` — append-only: правка автором в течение 5 минут заводит новую
 * строку со ссылкой `editOf` на предыдущую, а не переписывает её — тот же
 * приём, что у исправления записи паспорта зуба (`supersedesId`).
 *
 * Документация модуля: docs/03-modules/09-chat.md
 */

export const ChatSubjectType = z.enum(['patient', 'lab_order']);
export type ChatSubjectType = z.infer<typeof ChatSubjectType>;

export const ChatChannel = z.object({
  id: Id,
  subjectType: ChatSubjectType,
  subjectId: Id,
  createdAt: z.iso.datetime(),
  rowVersion: z.number().int().nonnegative(),
});
export type ChatChannel = z.infer<typeof ChatChannel>;

export const ChatMessage = z.object({
  id: Id,
  channelId: Id,
  subjectType: ChatSubjectType,
  subjectId: Id,
  authorId: Id,
  /** Системное сообщение (наряд создан/сменил статус) — без автора среди людей. */
  isSystem: z.boolean(),
  text: z.string().min(1).max(4000),
  mentionedUserIds: z.array(Id),
  /** Ссылка на зуб (`#26`) — только у канала наряда и карты пациента. */
  toothFdi: Fdi.nullable(),
  /** Предыдущая версия этого сообщения, если это правка. */
  editOf: Id.nullable(),
  createdAt: z.iso.datetime(),
  clientCreatedAt: z.iso.datetime(),
});
export type ChatMessage = z.infer<typeof ChatMessage>;

export const ChatMessageRead = z.object({
  channelId: Id,
  userId: Id,
  lastReadMessageId: Id,
  readAt: z.iso.datetime(),
});
export type ChatMessageRead = z.infer<typeof ChatMessageRead>;

/**
 * Отправка сообщения. Без `channelId` — см. заголовок файла. Правка того же
 * сообщения идёт тем же путём с заполненным `editOf`.
 */
export const SendChatMessageRequest = z.object({
  subjectType: ChatSubjectType,
  subjectId: Id,
  text: z.string().min(1).max(4000),
  mentionedUserIds: z.array(Id).max(20).default([]),
  toothFdi: Fdi.optional(),
  /** Правка предыдущего сообщения (в течение 5 минут после его отправки). */
  editOf: Id.optional(),
});
export type SendChatMessageRequest = z.infer<typeof SendChatMessageRequest>;

export const MarkChatReadRequest = z.object({
  lastReadMessageId: Id,
});
export type MarkChatReadRequest = z.infer<typeof MarkChatReadRequest>;

export const ChatChannelsQuery = z.object({
  subjectType: ChatSubjectType,
  subjectId: Id,
});
export type ChatChannelsQuery = z.infer<typeof ChatChannelsQuery>;

export const ChatChannelView = z.object({ channel: ChatChannel.nullable() });
export type ChatChannelView = z.infer<typeof ChatChannelView>;

export const ChatMessagesQuery = z.object({
  /** Курсор постраничного обхода — `id` последнего увиденного сообщения. */
  cursor: Id.optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
});
export type ChatMessagesQuery = z.infer<typeof ChatMessagesQuery>;

export const ChatMessages = z.object({ messages: z.array(ChatMessage) });
export type ChatMessages = z.infer<typeof ChatMessages>;

/** Непрочитанное по каналам, где у пользователя есть хоть одно сообщение после отметки. */
export const UnreadChatSummary = z.object({
  channels: z.array(z.object({ channelId: Id, subjectType: ChatSubjectType, subjectId: Id, unreadCount: z.number().int().nonnegative() })),
});
export type UnreadChatSummary = z.infer<typeof UnreadChatSummary>;
